import React, { useState, useRef, useEffect } from 'react';
import { posAPI } from '../services/api';
import './BarcodeScanner.css';

const BarcodeScanner = ({ onProductScanned }) => {
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  
  // Refs for tracking scan state
  const lastScannedBarcodeRef = useRef('');
  const isScanningRef = useRef(false);
  const scanTimeoutRef = useRef(null);
  const lastScanTimeRef = useRef(0);

  // Function to focus on barcode input
  const focusBarcodeInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(
        inputRef.current.value.length,
        inputRef.current.value.length
      );
    }
  };

  useEffect(() => {
    // Focus on input when component mounts
    focusBarcodeInput();

    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  // Mark user interaction when they focus on input
  const handleInputFocus = () => {
    setIsFocused(true);
    setUserInteracted(true);
  };

  // Auto-scan detection
  useEffect(() => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    if (barcodeInput.length === 18) {
      scanTimeoutRef.current = setTimeout(() => {
        handleAutoScan(barcodeInput);
      }, 200);
    }
  }, [barcodeInput]);

  const handleAutoScan = async (barcode) => {
    if (isScanningRef.current) {
      console.log('🛑 Scan already in progress, ignoring...');
      return;
    }

    if (barcode === lastScannedBarcodeRef.current) {
      console.log('🛑 Same barcode as last scan, ignoring...', barcode);
      return;
    }

    const now = Date.now();
    if (now - lastScanTimeRef.current < 1000) {
      console.log('🛑 Scan too soon after last scan, ignoring...');
      return;
    }

    if (barcode.length !== 18) {
      console.log('⚠️ Incomplete barcode, ignoring...');
      return;
    }

    console.log('🔍 Starting auto-scan:', barcode);
    
    isScanningRef.current = true;
    lastScannedBarcodeRef.current = barcode;
    lastScanTimeRef.current = now;
    
    setLoading(true);
    setError('');

    try {
      const response = await posAPI.scanBarcode(barcode);
      
      if (response.data && response.data.data && response.data.data.barcode) {
        const barcodeData = response.data.data.barcode;
        
        let productData;
        
        if (barcodeData.variant_id && barcodeData.variant) {
          productData = {
            id: `variant_${barcodeData.variant.id}`,
            name: `${barcodeData.product.name} - ${barcodeData.variant.variant_name}`,
            price: parseFloat(barcodeData.variant.sale_price || barcodeData.variant.price),
            base_product_id: barcodeData.product.id,
            variant: barcodeData.variant,
            barcode: barcodeData.barcode_number,
            stock_quantity: barcodeData.variant.stock_quantity,
            is_variant: true
          };
        } else if (barcodeData.product) {
          const currentPrice = getCurrentPrice(barcodeData.product);
          productData = {
            id: barcodeData.product.id.toString(),
            name: barcodeData.product.name,
            price: currentPrice,
            base_product_id: barcodeData.product.id,
            variant: null,
            barcode: barcodeData.barcode_number,
            stock_quantity: barcodeData.product.stock_quantity,
            is_variant: false
          };
        }

        if (productData) {
          console.log('✅ Product data ready, calling onProductScanned');
          onProductScanned(productData);
          setBarcodeInput('');
          setError('');
          
          setTimeout(() => {
            if (isFocused) {
              focusBarcodeInput();
            }
          }, 300);
        }
      } else {
        throw new Error('Invalid barcode response structure');
      }
    } catch (err) {
      console.error('❌ Barcode scan error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Barcode not found';
      setError(errorMessage);
      lastScannedBarcodeRef.current = '';
      
      setTimeout(() => {
        if (isFocused) {
          focusBarcodeInput();
        }
      }, 300);
    } finally {
      setLoading(false);
      setTimeout(() => {
        isScanningRef.current = false;
      }, 500);
    }
  };

  const handleManualScan = async () => {
    if (!barcodeInput.trim()) return;

    // Set user interaction for permissions
    setUserInteracted(true);

    if (isScanningRef.current) {
      setError('Scan already in progress, please wait...');
      return;
    }

    if (barcodeInput === lastScannedBarcodeRef.current) {
      setError('Same barcode as last scan, please scan a different barcode');
      return;
    }

    setLoading(true);
    setError('');

    isScanningRef.current = true;
    lastScannedBarcodeRef.current = barcodeInput;
    lastScanTimeRef.current = Date.now();

    try {
      const response = await posAPI.scanBarcode(barcodeInput);
      
      if (response.data && response.data.data && response.data.data.barcode) {
        const barcodeData = response.data.data.barcode;
        
        let productData;
        
        if (barcodeData.variant_id && barcodeData.variant) {
          productData = {
            id: `variant_${barcodeData.variant.id}`,
            name: `${barcodeData.product.name} - ${barcodeData.variant.variant_name}`,
            price: parseFloat(barcodeData.variant.sale_price || barcodeData.variant.price),
            base_product_id: barcodeData.product.id,
            variant: barcodeData.variant,
            barcode: barcodeData.barcode_number,
            stock_quantity: barcodeData.variant.stock_quantity,
            is_variant: true
          };
        } else if (barcodeData.product) {
          const currentPrice = getCurrentPrice(barcodeData.product);
          productData = {
            id: barcodeData.product.id.toString(),
            name: barcodeData.product.name,
            price: currentPrice,
            base_product_id: barcodeData.product.id,
            variant: null,
            barcode: barcodeData.barcode_number,
            stock_quantity: barcodeData.product.stock_quantity,
            is_variant: false
          };
        }

        if (productData) {
          onProductScanned(productData);
          setBarcodeInput('');
          setError('');
          
          setTimeout(() => {
            if (isFocused) {
              focusBarcodeInput();
            }
          }, 300);
        }
      } else {
        throw new Error('Invalid barcode response');
      }
    } catch (err) {
      console.error('Barcode scan error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Barcode not found';
      setError(errorMessage);
      lastScannedBarcodeRef.current = '';
      
      setTimeout(() => {
        if (isFocused) {
          focusBarcodeInput();
        }
      }, 300);
    } finally {
      setLoading(false);
      setTimeout(() => {
        isScanningRef.current = false;
      }, 500);
    }
  };

  const getCurrentPrice = (product) => {
    if (!product) return 0;
    
    try {
      const now = new Date();
      const saleStart = product.sale_start_date ? new Date(product.sale_start_date) : null;
      const saleEnd = product.sale_end_date ? new Date(product.sale_end_date) : null;
      
      if (product.sale_price && saleStart && saleEnd && now >= saleStart && now <= saleEnd) {
        return parseFloat(product.sale_price);
      }
      return parseFloat(product.price || 0);
    } catch (error) {
      return parseFloat(product.price || 0);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleManualScan();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualScan();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setBarcodeInput(value);
    
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
  };

  const clearInput = () => {
    setBarcodeInput('');
    setError('');
    lastScannedBarcodeRef.current = '';
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    if (isFocused) {
      focusBarcodeInput();
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="barcode-scanner">
      <h3>Barcode Scanner</h3>
      
      <form onSubmit={handleSubmit} className="barcode-form">
        <div className="scanner-input-group">
          <input
            ref={inputRef}
            type="text"
            value={barcodeInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder="Scan barcode (18 digits) or enter manually..."
            className="barcode-input"
            disabled={loading}
            autoFocus
          />
          {barcodeInput && (
            <button 
              type="button"
              className="clear-btn"
              onClick={clearInput}
              disabled={loading}
            >
              ✕
            </button>
          )}
          <button 
            type="submit" 
            className="scan-btn"
            disabled={loading || !barcodeInput.trim()}
          >
            {loading ? '🔍 Scanning...' : '🔍 Scan'}
          </button>
        </div>
        
        <div className="barcode-length-indicator">
          Length: {barcodeInput.length}/18 characters
          {barcodeInput.length === 18 && <span className="complete-indicator"> ✓ Complete</span>}
        </div>
      </form>

      {error && (
        <div className="scan-error">
          ❌ {error}
        </div>
      )}

      <div className="scanner-status">
        {loading ? (
          <div className="status-loading">🔄 Processing barcode...</div>
        ) : (
          <div className="status-ready">✅ Ready to scan</div>
        )}
      </div>
    </div>
  );
};

export default BarcodeScanner;